import { ResourceComposeRequest, resourceComposeScope, chatRequestActor } from '../../../services/Subjects/ChatSubjects';
import { appendResourceToDraft, resourceChat } from '../../../services/resources/ResourceChatService';
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useChatConnection } from "../../../contexts/ChatConnectionContext";
import { Conversation, MeetingRoomDto, GetMyChannelConversationAsync, Message, RemoveAdHocFromConversationDto, createConversation, getMyConversations, getAllConversations, markConversationAsRead } from "../../../services/chatService/ChatService";
import { getAllUsersInOrganization, getSuperAdminChatInitiationDirectory } from "../../../services/userServices/UserInfoService";
import UserInfo from "../../../interfaces/UserInfo";
import { conversationRead$ } from "../../../hooks/globalChatHooks/useGlobalChat";
import { toast } from "react-toastify";
import { isAutomatedBotConversation } from "../../../utilities/chat/conversationUtils";
import { useAuth } from "../../../firebase/AuthProvider";
import { MessageType } from "../../../Data/SD";
import { formatSystemMessage } from "../../../utilities/formatSystemMessage";
import { formatChatTimestamp, toChatTimestamp } from "../../../utilities/chat/relativeTimeUtils";
import { useNavigate } from "react-router-dom";
import CreateConversationModal from "./CreateConversationModal";
import { Search, Plus, Users, Hash, Archive, MessageSquare, Paperclip, Video, Bot, CheckCheck, Copy, Smile } from "lucide-react";
import { LobbyState } from "../../../interfaces/videocallDtos/videoCall";
import { encodeLobbyStateToToken } from "../../../utilities/lobbyStateToken";
import MilestoneAvatar from "../../ui/Avatar/MilestoneAvatar";
import { formatReactionCount, ReactionBadge } from "./PopupReactionActivity";
import { CHAT_DRAFT_CHANGED_EVENT, getConversationDraftPreview, getChatDraft, saveChatDraft, nextChatDraftRevision } from "../../../services/chatService/ChatDrafts";

export interface ConversationListRef {
  toggleAddConversation: (anchorEl?: HTMLElement | null) => void;
}

interface ConversationListProps {
  resourceComposeRequest?: ResourceComposeRequest | null;
  onResourceComposeFinished?: () => void;
  onResourceComposeBlocked?: () => void;
  authToken: string;
  userId: string;
  selectedConversationId: number | null;
  onSelectConversation: (conversation: Conversation) => void;
  width?: string;
  showHeader?: boolean;
  removeActiveMeetingFromSelectedConversation?: () => void;
  onCloseSideChat?: () => void;
  conversationRef?: React.RefObject<HTMLButtonElement>;
  handleConversation?: () => void;
  isSideView?: boolean;
  /** Rendered above the conversation rows (popup unread-reaction summary). */
  listTopSlot?: React.ReactNode;
  /** Unread reaction counts keyed by conversation id. */
  reactionBadges?: Record<number, ReactionBadge>;
}

/** Helpers */
const toTimestamp = (
  value: string | number | Date | null | undefined,
): number | null => toChatTimestamp(value);

const normalizeMessage = (m: Message): Message & { _ts?: number } => {
  const ts = toTimestamp((m as any).createDate ?? (m as any).createdAt);
  return { ...m, _ts: ts ?? undefined };
};

const extractRichTextPreview = (payload: unknown): string => {
  if (!payload) return "";
  if (typeof payload === "string") {
    try {
      return extractRichTextPreview(JSON.parse(payload));
    } catch {
      return "";
    }
  }
  if (typeof payload !== "object") return "";

  const node = payload as any;
  if (node.doc) return extractRichTextPreview(node.doc);
  if (node.type === "text") return node.text ?? "";
  if (node.type === "mention") return node.attrs?.label ? `@${node.attrs.label}` : "";
  if (!Array.isArray(node.content)) return "";
  return node.content.map(extractRichTextPreview).filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
};


type ConversationCallStatePayload = {
  conversationId?: number;
  ConversationId?: number;
  roomId?: string;
  RoomId?: string;
  status?: string;
  Status?: string;
  startTime?: string | Date;
  StartTime?: string | Date;
};

const activeMeetingFromCallState = (payload: ConversationCallStatePayload): MeetingRoomDto | undefined => {
  const status = String(payload.status ?? payload.Status ?? "").toLowerCase();
  if (status === "end" || status === "ended") return undefined;
  const roomId = payload.roomId ?? payload.RoomId;
  if (!roomId) return undefined;
  const start = new Date(payload.startTime ?? payload.StartTime ?? Date.now());
  return { id: roomId, roomId, createdAt: start, startDate: start, title: "Call in progress" };
};


const getParticipantLabel = (participant: any): string => {
  if (!participant) return "Unknown";

  const explicitName = [participant.firstName, participant.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    explicitName ||
    participant.displayName ||
    participant.email ||
    participant.userName ||
    "Unknown"
  ).trim();
};

const compactParticipantLabel = (participant: any): string => {
  const rawLabel = getParticipantLabel(participant);
  const looksLikeEmail = rawLabel.includes("@");

  if (looksLikeEmail) {
    const localPart = rawLabel.split("@")[0] || rawLabel;
    return localPart.length > 16 ? `${localPart.slice(0, 15)}…` : localPart;
  }

  if (rawLabel.length <= 18) return rawLabel;

  const words = rawLabel.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    const abbreviated = `${words[0].charAt(0)}. ${words[words.length - 1]}`;
    if (abbreviated.length <= 18) return abbreviated;
  }

  return `${rawLabel.slice(0, 17)}…`;
};

const getCompactConversationTitle = (participants: any[]): string => {
  if (!participants?.length) return "Conversation";

  if (participants.length === 1) {
    return getParticipantLabel(participants[0]);
  }

  const firstParticipant = compactParticipantLabel(participants[0]);
  const otherCount = participants.length - 1;
  return `${firstParticipant} & ${otherCount} ${otherCount === 1 ? "other" : "others"}`;
};

const ConversationList = React.forwardRef<ConversationListRef, ConversationListProps>(
  (props, ref) => {
    const {
      authToken,
      userId,
      selectedConversationId,
      onSelectConversation,
      removeActiveMeetingFromSelectedConversation,
      onCloseSideChat,
      conversationRef,
      isSideView = false,
      listTopSlot,
      reactionBadges,
    } = props;

    const { currentUser, currentRoles, currentUserInfo, impersonatedUserId } = useAuth();
    const isSuperAdmin = currentRoles?.some(
      (role) => role.name?.toUpperCase() === "SUPER ADMIN",
    ) ?? false;
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [channelConversations, setChannelConversations] = useState<Conversation[]>([]);
    // New unified state for the combined inbox list [Channel and Normal Conversation Together]
    const [unifiedConversations, setUnifiedConversations] = useState<Conversation[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingUnified, setLoadingUnified] = useState<boolean>(false);
    const [directory, setDirectory] = useState<{ scope: string; users: UserInfo[] }>({ scope: '', users: [] });
    const [recipientOwner, setRecipientOwner] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [addConversationView, setAddConversationView] = useState<boolean>(false);
    const [isCreatingConversation, setIsCreatingConversation] = useState(false);
    const composeScope = resourceComposeScope(currentUser?.uid ?? '', currentUserInfo?.id ?? '', currentUserInfo?.organization?.id,
      (currentRoles ?? []).map(role => role.name).sort().join('|'));
    const rawShare = props.resourceComposeRequest;
    const shareScope = rawShare ? resourceComposeScope(rawShare.userId, rawShare.employeeId, rawShare.organizationId, rawShare.roles, rawShare.actorId ?? '') : '';
    const activeShare = rawShare && shareScope === composeScope && !impersonatedUserId && !chatRequestActor() ? rawShare : null;
    const users = directory.scope === composeScope ? directory.users : [];
    const recipientOwnerKey = JSON.stringify([composeScope, activeShare?.requestId ?? null]);
    const recipientSurfaceCurrent = recipientOwner === recipientOwnerKey && (!rawShare || !!activeShare);
    const shareRef = useRef({ request: activeShare, scope: composeScope });
    shareRef.current = { request: activeShare, scope: composeScope };
    useEffect(() => () => { shareRef.current = { request: null, scope: '' }; }, []);
    const cancelledShare = useRef<number | null>(null);
    const lastShare = useRef<number | null>(null);
    useEffect(() => {
      if (activeShare && shareScope === composeScope) {
        lastShare.current = activeShare.requestId; setRecipientOwner(recipientOwnerKey); setSelectedUsers([]); setAddConversationView(true);
      } else if (lastShare.current !== null) {
        lastShare.current = null; setSelectedUsers([]); setAddConversationView(false);
      }
    }, [activeShare, shareScope, composeScope, recipientOwnerKey]);
    const closeRecipientSelection = () => { cancelledShare.current = activeShare?.requestId ?? null; shareRef.current.request = null; setSelectedUsers([]); setAddConversationView(false); props.onResourceComposeFinished?.(); };
    const listSurfaceRef = useRef<HTMLDivElement>(null);
    const returnFocusFrame = useRef<number | null>(null);
    useEffect(() => () => { if (returnFocusFrame.current !== null) window.cancelAnimationFrame(returnFocusFrame.current); }, []);
    const closeRecipientPicker = () => {
      const requestId = activeShare?.requestId;
      closeRecipientSelection();
      if (returnFocusFrame.current !== null) window.cancelAnimationFrame(returnFocusFrame.current);
      returnFocusFrame.current = window.requestAnimationFrame(() => {
        returnFocusFrame.current = null;
        if (shareRef.current.scope !== composeScope || (shareRef.current.request && shareRef.current.request.requestId !== requestId)) return;
        const surface = listSurfaceRef.current;
        if (!surface?.isConnected) return;
        const target = surface.querySelector<HTMLElement>('[aria-label="New private message"]') ?? surface.closest<HTMLElement>('[data-chat-primary-region]');
        target?.focus({ preventScroll: true });
      });
    };


    const isCreatingConversationRef = useRef(false);
    const [searchTerm, setSearchTearm] = useState<string>("");
    const [inboxFilter, setInboxFilter] = useState<"all" | "unread" | "channels">("all");
    const [conversationContextMenu, setConversationContextMenu] = useState<{
      x: number;
      y: number;
      conversation: Conversation;
      title: string;
    } | null>(null);
    const conversationContextMenuRef = useRef<HTMLDivElement>(null);
    const [, setPopupStyle] = useState<React.CSSProperties>({});
    const [relativeTimeNow, setRelativeTimeNow] = useState(() => Date.now());
    const [, setDraftRevision] = useState(0);
    const internalAddButtonRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const intervalId = window.setInterval(() => {
        setRelativeTimeNow(Date.now());
      }, 30_000);

      return () => window.clearInterval(intervalId);
    }, []);

    useEffect(() => {
      const handleDraftChanged = (event: Event) => {
        const detail = (event as CustomEvent<{ userId?: string }>).detail;
        if (!detail?.userId || detail.userId === userId) setDraftRevision(revision => revision + 1);
      };
      window.addEventListener(CHAT_DRAFT_CHANGED_EVENT, handleDraftChanged);
      return () => window.removeEventListener(CHAT_DRAFT_CHANGED_EVENT, handleDraftChanged);
    }, [userId]);

    useEffect(() => {
      if (!conversationContextMenu) return;
      const closeMenu = (event: MouseEvent) => {
        if (!conversationContextMenuRef.current?.contains(event.target as Node)) {
          setConversationContextMenu(null);
        }
      };
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") setConversationContextMenu(null);
      };
      document.addEventListener("mousedown", closeMenu);
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("mousedown", closeMenu);
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [conversationContextMenu]);

    const [, setActiveTab] = useState<"Conversations" | "Channels">("Conversations");

    // New state for archived channels support
    const [showArchived, setShowArchived] = useState<boolean>(false);
    const [archivedPermissionError, setArchivedPermissionError] = useState<string | null>(null);
    const [useUnifiedEndpoint] = useState<boolean>(true); // Feature flag to switch between old and new

    // A map: conversationId -> array of userIds who are typing
    const [typingMap, setTypingMap] = useState<Record<number, string[]>>({});

    // Shared SignalR connection
    const { connection } = useChatConnection();

    const navigate = useNavigate();

    // Refs to avoid stale values in handlers
    const selectedConversationIdRef = useRef<number | null>(selectedConversationId);
    useEffect(() => {
      selectedConversationIdRef.current = selectedConversationId;
    }, [selectedConversationId]);

    const currentUserId = currentUser?.uid;
    const directoryRequestId = useRef(0);
    const loadChatInitiationDirectory = useCallback(async (token: string) => {
      const requestId = ++directoryRequestId.current;
      setDirectory({ scope: composeScope, users: [] });
      try {
        const res = await (isSuperAdmin
          ? getSuperAdminChatInitiationDirectory(token)
          : getAllUsersInOrganization(token, true));
        if (requestId !== directoryRequestId.current || shareRef.current.scope !== composeScope) return;
        setDirectory({ scope: composeScope, users: (res || []).filter(user => user.id !== currentUserId && user.id !== currentUserInfo?.id) });
      } catch (error: any) {
        if (requestId !== directoryRequestId.current || shareRef.current.scope !== composeScope) return;
        toast.error(error?.message || "Failed to load chat directory.");
      }
    }, [isSuperAdmin, currentUserId, currentUserInfo?.id, composeScope]);

    // fetch initial lists
    useEffect(() => {
      if (authToken) {
        if (useUnifiedEndpoint) {
          getAllUnifiedConversations(authToken, showArchived);
        } else {
          // Legacy: keep existing behavior
          getMyConversation(authToken);
          getChannleConversation(authToken, userId);
        }
        loadChatInitiationDirectory(authToken);
      } else {
        setDirectory({ scope: composeScope, users: [] });
      }
      return () => { directoryRequestId.current += 1; };
    }, [authToken, userId, useUnifiedEndpoint, showArchived, isSuperAdmin, loadChatInitiationDirectory, composeScope]);

    // external read events -> clear unread
    useEffect(() => {
      const subscription = conversationRead$.subscribe((readConversationId) => {
        const now = new Date();
        setConversations((prev) =>
          prev.map((convo) =>
            convo.id === readConversationId
              ? { ...convo, unreadCount: 0, lastReadAt: now }
              : convo
          )
        );
        setChannelConversations((prev) =>
          prev.map((convo) =>
            convo.id === readConversationId
              ? { ...convo, unreadCount: 0, lastReadAt: now }
              : convo
          )
        );
        if (useUnifiedEndpoint) {
          setUnifiedConversations((prev) =>
            prev.map((convo) =>
              convo.id === readConversationId
                ? { ...convo, unreadCount: 0, lastReadAt: now }
                : convo
            )
          );
        }
      });

      return () => subscription.unsubscribe();
    }, [useUnifiedEndpoint]);

    const getAllUnifiedConversations = async (token: string, includeArchived: boolean = true) => {
      setLoadingUnified(true);
      setArchivedPermissionError(null);

      try {
        const response = await getAllConversations(token, true, includeArchived);

        if (response.success && response.data) {
          setUnifiedConversations(response.data);
        } else {
          // Handle 403 Permission Denied for archived channels
          if (response.statusCode === 403) {
            setArchivedPermissionError(response.message || 'You don\'t have permission to view archived channels');
            // Fallback: fetch without archived if permission denied
            if (includeArchived) {
              const fallbackResponse = await getAllConversations(token, true, false);
              if (fallbackResponse.success && fallbackResponse.data) {
                setUnifiedConversations(fallbackResponse.data);
              }
              setShowArchived(false); // Reset toggle
            }
          } else {
            setError(response.message || 'Failed to load conversations');
          }
        }
      } catch (err: any) {
        console.error('Failed to fetch unified conversations:', err);
        setError(err.message || 'Failed to load conversations');
      } finally {
        setLoadingUnified(false);
      }
    };

    const getChannleConversation = (token: string, uid: string) => {
      GetMyChannelConversationAsync(token, uid).then((res) => {
        if (res.success) {
          setChannelConversations(res.data || []);
        }
      });
    };

    const getMyConversation = (token: string) => {
      setLoading(true);
      getMyConversations(token).then((res) => {
        if (res) {
          setConversations(res);
        }
      }).finally(() => {
        setLoading(false);
      })
    };

    // attach SignalR handlers once connection is ready
    // useEffect(() => {
    //   if (!connection) return;

    //   // Placeholder for RemoveAdHocFromConversation event
    //   const handleRemoveAdHocFromConversation = (
    //     payload: RemoveAdHocFromConversationDto
    //   ) => {

    //     if (selectedConversationId == payload.conversationId) {
    //       if (removeActiveMeetingFromSelectedConversation) {
    //         removeActiveMeetingFromSelectedConversation();
    //       }
    //     }

    //     setConversations(prevConversations =>
    //       prevConversations.map(conversation =>
    //         conversation.id === payload.conversationId
    //           ? { ...conversation, activeMeeting: undefined }
    //           : conversation
    //       )
    //     );

    //     if (useUnifiedEndpoint) {
    //       setUnifiedConversations(prevConversations =>
    //         prevConversations.map(conversation =>
    //           conversation.id === payload.conversationId
    //             ? { ...conversation, activeMeeting: undefined }
    //             : conversation
    //         )
    //       );
    //     }
    //   };


    //   const handleTypingStatus = (
    //     convId: number,
    //     typingUserId: string,
    //     currentlyTyping: boolean
    //   ) => {
    //     if (typingUserId === userId) return;
    //     setTypingMap((prev) => {
    //       const currentList = prev[convId] || [];
    //       if (currentlyTyping) {
    //         if (!currentList.includes(typingUserId)) {
    //           return { ...prev, [convId]: [...currentList, typingUserId] };
    //         }
    //         return prev;
    //       } else {
    //         return {
    //           ...prev,
    //           [convId]: currentList.filter((id) => id !== typingUserId),
    //         };
    //       }
    //     });
    //   };

    //   /** NEW: bump unread for messages not authored by me & not in active convo */
    //   const handleReceiveMessage = (rawMessage: Message) => {
    //     const message = normalizeMessage(rawMessage);
    //     const msgTs = (message as any)._ts ?? toTimestamp((message as any).createDate) ?? 0;
    //     const isMine = message.author?.id === userId;
    //     const activeId = selectedConversationIdRef.current;

    //     // Update direct conversations
    //     setConversations((prev) => {
    //       const idx = prev.findIndex((c) => c.id === message.conversationId);
    //       if (idx === -1) return prev;

    //       const c = prev[idx];
    //       const shouldIncrement = !isMine && c.id !== activeId;
    //       const unreadCount = shouldIncrement ? (c.unreadCount ?? 0) + 1 : c.unreadCount ?? 0;

    //       const updated: Conversation = {
    //         ...c,
    //         lastMessage: message as any,
    //         unreadCount,
    //       };

    //       const without = prev.filter((x) => x.id !== c.id);
    //       return [updated, ...without];
    //     });

    //     // Also try to update channels (if any share id)
    //     setChannelConversations((prev) => {
    //       const idx = prev.findIndex((c) => c.id === message.conversationId);
    //       if (idx === -1) return prev;

    //       const c = prev[idx];
    //       const shouldIncrement = !isMine && c.id !== activeId;
    //       const unreadCount = shouldIncrement ? (c.unreadCount ?? 0) + 1 : c.unreadCount ?? 0;

    //       const updated: Conversation = {
    //         ...c,
    //         lastMessage: message as any,
    //         unreadCount,
    //       };

    //       const without = prev.filter((x) => x.id !== c.id);
    //       return [updated, ...without];
    //     });

    //     if (useUnifiedEndpoint) {
    //       setUnifiedConversations((prev) => {
    //         const idx = prev.findIndex((c) => c.id === message.conversationId);
    //         if (idx === -1) return prev;

    //         const c = prev[idx];
    //         const shouldIncrement = !isMine && c.id !== activeId;
    //         const unreadCount = shouldIncrement ? (c.unreadCount ?? 0) + 1 : c.unreadCount ?? 0;

    //         const updated: Conversation = {
    //           ...c,
    //           lastMessage: message as any,
    //           unreadCount,
    //         };

    //         const without = prev.filter((x) => x.id !== c.id);
    //         return [updated, ...without];
    //       });
    //     }
    //   };

    //   /** Normalize and clear unread when *I* read */
    //   const handleReceiveReadReceipt = (
    //     convId: number,
    //     userIdWhoRead: string,
    //     lastReadAt: string | Date
    //   ) => {
    //     if (userIdWhoRead !== userId) return;
    //     const ts = toTimestamp(lastReadAt);
    //     const date = ts ? new Date(ts) : new Date();

    //     setConversations((prev) =>
    //       prev.map((c) =>
    //         c.id === convId ? { ...c, unreadCount: 0, lastReadAt: date } : c
    //       )
    //     );
    //     setChannelConversations((prev) =>
    //       prev.map((c) =>
    //         c.id === convId ? { ...c, unreadCount: 0, lastReadAt: date } : c
    //       )
    //     );

    //     if (useUnifiedEndpoint) {
    //       setUnifiedConversations((prev) =>
    //         prev.map((c) =>
    //           c.id === convId ? { ...c, unreadCount: 0, lastReadAt: date } : c
    //         )
    //       );
    //     }
    //   };

    //   const handleConversationCreated = (newConv: Conversation) => {
    //     setConversations((prev) => {
    //       const exists = prev.some((c) => c.id === newConv.id);
    //       if (exists) return prev;
    //       return [newConv, ...prev];
    //     });
    //     // Also add to unified list if using unified endpoint
    //     if (useUnifiedEndpoint) {
    //       setUnifiedConversations((prev) => {
    //         const exists = prev.some((c) => c.id === newConv.id);
    //         if (exists) return prev;
    //         return [newConv, ...prev];
    //       });
    //     }
    //   };

    //   connection.on("ReceiveTypingStatus", handleTypingStatus);
    //   connection.on("ReceiveMessage", handleReceiveMessage);
    //   connection.on("ReceiveReadReceipt", handleReceiveReadReceipt);
    //   connection.on("ConversationCreated", handleConversationCreated);

    //   // Register RemoveAdHocFromConversation event
    //   connection.on("RemoveAdHocFromConversation", handleRemoveAdHocFromConversation);

    //   return () => {
    //     connection.off("ReceiveTypingStatus", handleTypingStatus);
    //     connection.off("ReceiveMessage", handleReceiveMessage);
    //     connection.off("ReceiveReadReceipt", handleReceiveReadReceipt);
    //     connection.off("ConversationCreated", handleConversationCreated);
    //     connection.off("RemoveAdHocFromConversation", handleRemoveAdHocFromConversation);
    //   };
    // }, [connection, userId]);

    // join groups when conversations change
    // useEffect(() => {
    //   if (!connection) return;
    //   const conversationsToJoin = useUnifiedEndpoint ? unifiedConversations : conversations;
    //   conversationsToJoin.forEach((conv) => {
    //     connection
    //       .invoke("JoinConversation", conv.id)
    //       .catch((err) => console.error(`Failed to join conversation ${conv.id}`, err));
    //   });
    // }, [connection, conversations, unifiedConversations, useUnifiedEndpoint]);

    // Attach SignalR handlers once the shared connection is ready.
//
// The conversation list deliberately does not join conversation groups.
// New-message summaries and typing states are delivered through
// user-targeted events from ChatHub.
useEffect(() => {
  if (!connection) return;

  const handleRemoveAdHocFromConversation = (
    payload: RemoveAdHocFromConversationDto
  ) => {
    if (
      selectedConversationIdRef.current ===
      payload.conversationId
    ) {
      removeActiveMeetingFromSelectedConversation?.();
    }

    setConversations((previous) =>
      previous.map((conversation) =>
        conversation.id === payload.conversationId
          ? {
              ...conversation,
              activeMeeting: undefined,
            }
          : conversation
      )
    );

    setChannelConversations((previous) =>
      previous.map((conversation) =>
        conversation.id === payload.conversationId
          ? {
              ...conversation,
              activeMeeting: undefined,
            }
          : conversation
      )
    );

    setUnifiedConversations((previous) =>
      previous.map((conversation) =>
        conversation.id === payload.conversationId
          ? {
              ...conversation,
              activeMeeting: undefined,
            }
          : conversation
      )
    );
  };

  const handleConversationCallStateChanged = (payload: ConversationCallStatePayload) => {
    const conversationId = payload.conversationId ?? payload.ConversationId;
    if (!conversationId) return;
    const activeMeeting = activeMeetingFromCallState(payload);
    const applyMeetingState = (previous: Conversation[]) =>
      previous.map((conversation) =>
        conversation.id === conversationId ? { ...conversation, activeMeeting } : conversation
      );
    setConversations(applyMeetingState);
    setChannelConversations(applyMeetingState);
    setUnifiedConversations(applyMeetingState);
  };

  const handleTypingStatus = (
    conversationId: number,
    typingUserId: string,
    currentlyTyping: boolean
  ) => {
    if (
      !conversationId ||
      !typingUserId ||
      typingUserId === userId
    ) {
      return;
    }

    setTypingMap((previous) => {
      const currentUsers =
        previous[conversationId] ?? [];

      if (currentlyTyping) {
        if (
          currentUsers.includes(
            typingUserId
          )
        ) {
          return previous;
        }

        return {
          ...previous,
          [conversationId]: [
            ...currentUsers,
            typingUserId,
          ],
        };
      }

      const remainingUsers =
        currentUsers.filter(
          (id) => id !== typingUserId
        );

      if (
        remainingUsers.length ===
        currentUsers.length
      ) {
        return previous;
      }

      return {
        ...previous,
        [conversationId]:
          remainingUsers,
      };
    });
  };

  const updateConversationList = (
    previous: Conversation[],
    rawMessage: Message
  ): Conversation[] => {
    const message =
      normalizeMessage(rawMessage);

    // Replies belong to their thread. They may change unread attention state,
    // but they must never replace the conversation's top-level preview.
    if (message.parentMessageId != null || message.threadRootMessageId != null) {
      return previous;
    }

    const index =
      previous.findIndex(
        (conversation) =>
          conversation.id ===
          message.conversationId
      );

    if (index === -1) {
      return previous;
    }

    const currentConversation =
      previous[index];

    /*
     * The currently open conversation can receive:
     *
     * 1. ReceiveMessage through its active group
     * 2. ReceiveConversationListMessage through Clients.User
     *
     * Compare message IDs so that the same delivery does not increment
     * unreadCount twice.
     */
    if (
      currentConversation.lastMessage
        ?.id === message.id
    ) {
      return previous;
    }

    // Conversation-list delivery is synchronization only. Targeted attention
    // events below own unread state so uninvolved thread replies stay silent.
    const shouldIncrementUnread = false;

    const unreadCount =
      shouldIncrementUnread
        ? (
            currentConversation.unreadCount ??
            0
          ) + 1
        : currentConversation.unreadCount ??
          0;

    const updatedConversation: Conversation =
      {
        ...currentConversation,
        lastMessage: message as any,
        unreadCount,
      };

    return [
      updatedConversation,
      ...previous.filter(
        (conversation) =>
          conversation.id !==
          message.conversationId
      ),
    ];
  };

  const handleReceiveMessage = (
    message: Message
  ) => {
    if (
      !message ||
      !message.conversationId
    ) {
      return;
    }

    setConversations((previous) =>
      updateConversationList(
        previous,
        message
      )
    );

    setChannelConversations(
      (previous) =>
        updateConversationList(
          previous,
          message
        )
    );

    setUnifiedConversations(
      (previous) =>
        updateConversationList(
          previous,
          message
        )
    );
  };

  const handleReceiveAttention = (message: Message) => {
    if (!message?.conversationId || message.author?.id === userId) return;
    if (selectedConversationIdRef.current === message.conversationId) return;

    const incrementUnread = (previous: Conversation[]) => previous.map(conversation =>
      conversation.id === message.conversationId
        ? {
            ...conversation,
            lastMessage: message.parentMessageId != null || message.threadRootMessageId != null
              ? conversation.lastMessage
              : normalizeMessage(message) as any,
            unreadCount: (conversation.unreadCount ?? 0) + 1,
          }
        : conversation
    );

    setConversations(incrementUnread);
    setChannelConversations(incrementUnread);
    setUnifiedConversations(incrementUnread);
  };

  const handleReceiveReadReceipt = (
    conversationId: number,
    userIdWhoRead: string,
    lastReadAt: string | Date
  ) => {
    if (
      userIdWhoRead !== userId
    ) {
      return;
    }

    const timestamp =
      toTimestamp(lastReadAt);

    const readDate = timestamp
      ? new Date(timestamp)
      : new Date();

    const applyReadState = (
      previous: Conversation[]
    ) =>
      previous.map((conversation) =>
        conversation.id ===
        conversationId
          ? {
              ...conversation,
              unreadCount: 0,
              lastReadAt: readDate,
            }
          : conversation
      );

    setConversations(
      applyReadState
    );

    setChannelConversations(
      applyReadState
    );

    setUnifiedConversations(
      applyReadState
    );
  };

  const handleConversationCreated = (
    newConversation: Conversation
  ) => {
    setConversations((previous) => {
      const exists =
        previous.some(
          (conversation) =>
            conversation.id ===
            newConversation.id
        );

      return exists
        ? previous
        : [
            newConversation,
            ...previous,
          ];
    });

    setUnifiedConversations(
      (previous) => {
        const exists =
          previous.some(
            (conversation) =>
              conversation.id ===
              newConversation.id
          );

        return exists
          ? previous
          : [
              newConversation,
              ...previous,
            ];
      }
    );
  };

  /*
   * ReceiveMessage remains useful for the currently open conversation.
   * ReceiveConversationListMessage handles every conversation without
   * requiring mass group membership.
   *
   * Legacy user-targeted names remain subscribed during rollout so the
   * frontend works before and after the backend deployment.
   */
  connection.on(
    "ReceiveMessage",
    handleReceiveMessage
  );

  connection.on(
    "ReceiveConversationListMessage",
    handleReceiveMessage
  );

  connection.on(
    "ReceiveChatAttention",
    handleReceiveAttention
  );

  connection.on(
    "ReceiveMessageForUserASNotification",
    handleReceiveMessage
  );

  connection.on(
    "ReceiveMessageForParticipant",
    handleReceiveMessage
  );

  connection.on(
    "ReceiveTypingStatusForUser",
    handleTypingStatus
  );

  // Temporary compatibility with the previous group-based event.
  connection.on(
    "ReceiveTypingStatus",
    handleTypingStatus
  );

  connection.on(
    "ReceiveReadReceipt",
    handleReceiveReadReceipt
  );

  connection.on(
    "ConversationCreated",
    handleConversationCreated
  );

  connection.on(
    "RemoveAdHocFromConversation",
    handleRemoveAdHocFromConversation
  );

  connection.on(
    "ConversationCallStateChanged",
    handleConversationCallStateChanged
  );

  return () => {
    connection.off(
      "ReceiveMessage",
      handleReceiveMessage
    );

    connection.off(
      "ReceiveConversationListMessage",
      handleReceiveMessage
    );

    connection.off(
      "ReceiveChatAttention",
      handleReceiveAttention
    );

    connection.off(
      "ReceiveMessageForUserASNotification",
      handleReceiveMessage
    );

    connection.off(
      "ReceiveMessageForParticipant",
      handleReceiveMessage
    );

    connection.off(
      "ReceiveTypingStatusForUser",
      handleTypingStatus
    );

    connection.off(
      "ReceiveTypingStatus",
      handleTypingStatus
    );

    connection.off(
      "ReceiveReadReceipt",
      handleReceiveReadReceipt
    );

    connection.off(
      "ConversationCreated",
      handleConversationCreated
    );

    connection.off(
      "RemoveAdHocFromConversation",
      handleRemoveAdHocFromConversation
    );

    connection.off(
      "ConversationCallStateChanged",
      handleConversationCallStateChanged
    );
  };
}, [
  connection,
  userId,
  removeActiveMeetingFromSelectedConversation,
]);

    // ----- CREATE NEW CONVERSATION -----
    const getExistingConversationForSelection = (conversationList: Conversation[]) => {
      const desiredParticipantIds = new Set([userId, ...selectedUsers]);

      return conversationList.find((conversation) => {
        const isChannelConversation =
          conversation.isChannel === true ||
          (conversation.isChannel === undefined && Boolean(conversation.channel));

        if (isChannelConversation) return false;

        const participantIds = new Set(
          (conversation.participants || []).map((participant) => participant.id),
        );

        if (participantIds.size !== desiredParticipantIds.size) return false;

        return Array.from(desiredParticipantIds).every((id) => participantIds.has(id));
      });
    };

    const openExistingConversation = (conversation: Conversation) => {
      setSelectedUsers([]);
      setAddConversationView(false);
      onSelectConversation(conversation);
    };

    const handleCreateConversation = async () => {
      if ((rawShare && !activeShare) || !recipientSurfaceCurrent) return;
      if (activeShare) {
        if (impersonatedUserId || chatRequestActor() || activeShare.actorId || isCreatingConversationRef.current || shareScope !== composeScope || !selectedUsers.length || !currentUser || !currentUserInfo?.id) return;
        const request = activeShare;
        const stillCurrent = () => chatRequestActor() === (request.actorId ?? '') && cancelledShare.current !== request.requestId && shareRef.current.request?.requestId === request.requestId && shareRef.current.scope === shareScope;
        isCreatingConversationRef.current = true; setIsCreatingConversation(true);
        let blockedComposer = '';
        const checkComposer = () => {
          const detail = { userId: request.userId, blockedReason: '' };
          window.dispatchEvent(new CustomEvent('chat-resource-share-check', { detail }));
          if (detail.blockedReason) { blockedComposer = detail.blockedReason; throw new Error('Composer has unsaved context'); }
        };
        try {
          const token = await currentUser.getIdToken();
          if (!stillCurrent()) return;
          const preview = await resourceChat.preview(token, request.resourceId);
          if (!stillCurrent()) return;
          const directory = await (isSuperAdmin ? getSuperAdminChatInitiationDirectory(token) : getAllUsersInOrganization(token, true));
          if (!stillCurrent()) return;
          const eligible = (directory ?? []).filter(user => user.id !== userId && user.id !== currentUserInfo.id && (user as UserInfo & { isParticipant?: boolean }).isParticipant !== true);
          if (selectedUsers.some(id => !eligible.some(user => user.id === id))) throw new Error('Recipient unavailable');
          checkComposer();
          const response = await createConversation(token, selectedUsers, shareScope);
          if (!stillCurrent()) return;
          const conversation = response.data;
          if (!conversation?.id) throw new Error('Conversation unavailable');
          const writable = await resourceChat.conversations(token, undefined, conversation.id);
          if (!stillCurrent()) return;
          if (!writable.some(row => row.id === conversation.id)) throw new Error('Conversation unavailable');
          checkComposer();
          // Flush the live editor before merging its debounced draft.
          const flushDetail = { userId: request.userId, conversationId: conversation.id, failed: false, blockedReason: '' };
          window.dispatchEvent(new CustomEvent('chat-draft-flush', { detail: flushDetail }));
          if (flushDetail.blockedReason) {
            closeRecipientSelection(); onSelectConversation(conversation); toast.info(flushDetail.blockedReason); return;
          }
          if (flushDetail.failed) throw new Error('Draft could not be saved');
          const draft = getChatDraft(request.userId, conversation.id);
          const appended = appendResourceToDraft(draft?.doc, draft?.plaintext ?? '', request.resourceId, preview.title);
          if (!saveChatDraft({ userId: request.userId, conversationId: conversation.id, ...appended, updatedAt: nextChatDraftRevision() })) throw new Error('Draft could not be saved');
          window.dispatchEvent(new CustomEvent('resource-chat-draft', { detail: { userId: request.userId, conversationId: conversation.id } }));
          closeRecipientSelection(); onSelectConversation(conversation);
        } catch (error) {
          if (stillCurrent() && blockedComposer) {
            closeRecipientSelection(); props.onResourceComposeBlocked?.(); toast.info(blockedComposer);
          } else if (stillCurrent()) toast.error(error instanceof Error && error.message === 'Draft could not be saved'
            ? 'Your message draft could not be saved. Nothing was sent. Try again.'
            : 'The resource or recipients are no longer available. Your message was not sent.');
        }
        finally { isCreatingConversationRef.current = false; setIsCreatingConversation(false); }
        return;
      }
      // The service also de-duplicates identical in-flight requests, but keep a
      // synchronous UI-level guard so repeated clicks cannot start overlapping
      // create flows before React has rendered the disabled state.
      if (isCreatingConversationRef.current) return;

      if (!selectedUsers.length) {
        toast.info("Choose at least one person to start a chat.");
        return;
      }

      // Avoid intentionally asking the API to create a participant combination
      // that is already visible in the user's inbox. This handles both DMs and
      // group chats.
      const currentList = useUnifiedEndpoint
        ? unifiedConversations
        : [...conversations, ...channelConversations];
      const existingConversation = getExistingConversationForSelection(currentList);

      if (existingConversation) {
        openExistingConversation(existingConversation);
        return;
      }

      isCreatingConversationRef.current = true;
      setIsCreatingConversation(true);

      try {
        const conResponse = await createConversation(authToken, selectedUsers);

        if (conResponse.success && conResponse.data) {
          setSelectedUsers([]);
          setAddConversationView(false);
          onSelectConversation(conResponse.data);
          return;
        }

        if (conResponse?.data?.id) {
          openExistingConversation(conResponse.data);
          return;
        }

        // The local list can be stale (another tab/device may have created the
        // same chat). Refresh once and resolve the exact participant set before
        // showing an error to the user.
        const refreshedResponse = await getAllConversations(authToken, true, false);
        if (refreshedResponse.success && refreshedResponse.data) {
          setUnifiedConversations(refreshedResponse.data);
          const refreshedExisting = getExistingConversationForSelection(refreshedResponse.data);
          if (refreshedExisting) {
            openExistingConversation(refreshedExisting);
            return;
          }
        }

        toast.error(conResponse.message || "Unable to start this conversation.");
      } catch (err: any) {
        console.error("Failed to create conversation:", err);

        try {
          const refreshedResponse = await getAllConversations(authToken, true, false);
          if (refreshedResponse.success && refreshedResponse.data) {
            setUnifiedConversations(refreshedResponse.data);
            const refreshedExisting = getExistingConversationForSelection(refreshedResponse.data);
            if (refreshedExisting) {
              openExistingConversation(refreshedExisting);
              return;
            }
          }
        } catch (refreshError) {
          console.error("Failed to recover existing conversation after create conflict:", refreshError);
        }

        toast.error(err?.message || "Unable to start this conversation.");
      } finally {
        isCreatingConversationRef.current = false;
        setIsCreatingConversation(false);
      }
    };

    const toggleAddConversationView = (anchorEl?: HTMLElement | null) => {
      setAddConversationView((prev) => {
        const isOpening = !prev;
        if (isOpening) {
          setRecipientOwner(recipientOwnerKey);
          const targetEl = anchorEl || internalAddButtonRef.current;
          if (targetEl) {
            const rect = targetEl.getBoundingClientRect();
            setPopupStyle({
              position: "fixed",
              top: `${rect.bottom + 5}px`,
              left: `${rect.right}px`,
              transform: "translateX(-100%)",
            });
          } else {
            setPopupStyle({
              position: "absolute",
              top: "60px",
              right: "20px",
            });
          }
        }
        return isOpening;
      });
    };

    React.useImperativeHandle(ref, () => ({
      toggleAddConversation: (anchorEl?: HTMLElement | null) => {
        toggleAddConversationView(anchorEl);
      },
    }));

    // ----- RENDER -----
    // if (loading) return <div>Loading conversations...</div>;
    if ((rawShare && !activeShare) || (addConversationView && !recipientSurfaceCurrent)) {
      return <div role="status" className="dark-surface p-3 text-sm">Loading messages...</div>;
    }
    if (error) return <div>Error: {error}</div>;

    let list: Conversation[] = [];

    if (useUnifiedEndpoint) {
      // No conditions — render everything
      list = unifiedConversations;
    } else {
      // Legacy — merge both
      list = [...conversations, ...channelConversations];
    }


    const isConversationRead = (conv: Conversation) => {
      const lastMsg = conv.lastMessage as any | undefined;
      if (!lastMsg) return true;
      if (lastMsg.author?.id === userId) return true;
      if (typeof conv.unreadCount === "number") return conv.unreadCount === 0;

      const lastMessageTs = toTimestamp(lastMsg.createDate);
      const lastReadTs = toTimestamp(conv.lastReadAt);
      return lastMessageTs !== null && lastReadTs !== null && lastReadTs >= lastMessageTs;
    };

    const filteredConversations = list.filter((conv) => {
      const searchLower = searchTerm.trim().toLowerCase();
      const isChannelConversation =
        conv.isChannel === true ||
        (conv.isChannel === undefined && Boolean(conv.channel));

      if (inboxFilter === "unread" && isConversationRead(conv)) return false;
      if (inboxFilter === "channels" && !isChannelConversation) return false;

      if (!searchLower) return true;

      if (conv.channel?.name?.toLowerCase().includes(searchLower)) return true;

      return conv.participants.some((participant) => {
        if (participant.id === userId) return false;
        const name = `${participant.displayName || ""} ${participant.firstName || ""} ${
          participant.lastName || ""
        }`.toLowerCase();
        return name.includes(searchLower);
      });
    });

    // Optimistically mark read on selection (instant UI)
    const handleSelectConversation = (conv: Conversation) => {
      // Local state: unread -> 0
      setConversations((prev) =>
        prev.map((c) => (c.id === conv.id ? { ...c, unreadCount: 0, lastReadAt: new Date() } : c))
      );
      setChannelConversations((prev) =>
        prev.map((c) => (c.id === conv.id ? { ...c, unreadCount: 0, lastReadAt: new Date() } : c))
      );

      if (useUnifiedEndpoint) {
        setUnifiedConversations((prev) =>
          prev.map((c) => (c.id === conv.id ? { ...c, unreadCount: 0, lastReadAt: new Date() } : c))
        );
      }
      // Publish to anyone else listening
      conversationRead$.next(conv.id);
      // Upstream select
      onSelectConversation(conv);
    };

    const handleMarkConversationAsRead = async (conv: Conversation) => {
      setConversationContextMenu(null);
      try {
        await markConversationAsRead(authToken, conv.id);
        const readAt = new Date();
        const markRead = (items: Conversation[]) =>
          items.map((item) => item.id === conv.id
            ? { ...item, unreadCount: 0, lastReadAt: readAt }
            : item);
        setConversations(markRead);
        setChannelConversations(markRead);
        setUnifiedConversations(markRead);
        conversationRead$.next(conv.id);
      } catch (error: any) {
        toast.error(error?.message || "Failed to mark conversation as read.");
      }
    };

    const handleCopyConversationName = async (name: string) => {
      setConversationContextMenu(null);
      try {
        await navigator.clipboard.writeText(name);
        toast.info("Conversation name copied.");
      } catch {
        toast.error("Unable to copy conversation name.");
      }
    };

    const handleJoinActiveMeeting = (event: React.MouseEvent | React.KeyboardEvent, conv: Conversation) => {
      event.stopPropagation();
      const activeMeeting = conv.activeMeeting;
      if (!activeMeeting?.roomId) return;
      const state: LobbyState = {
        calenderEventId: activeMeeting.calendarEventId ?? null,
        relatedChatConversationId: conv.id.toString(),
        isPublisher: false,
        isOneToOneCall: conv.participants.length === 2,
      };
      const stateToken = encodeLobbyStateToToken(state);
      navigate(`/lobby/${activeMeeting.roomId}?state=${encodeURIComponent(stateToken)}`);
    };

    const formatTime = (date?: Date | string) => {
      return formatChatTimestamp(date, relativeTimeNow);
    };

    return (
      <>
        {!isSideView && addConversationView && (
          <CreateConversationModal
            addConversationView={addConversationView}
            users={users}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
            handleCreateConversation={handleCreateConversation}
            isCreating={isCreatingConversation}
            prepareResource={!!activeShare}
            resourceTitle={activeShare?.resourceTitle}
            onClose={closeRecipientPicker}
            chatClose={() => onCloseSideChat?.()}
            showOrganizations={isSuperAdmin}
          />
        )}

        <div
          ref={listSurfaceRef}
          className={`dark-surface common_card myconversations_card flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-white p-0 dark:bg-slate-900 ${
            isSideView ? "rounded-none border-0" : "rounded-l-[15px] border-r border-slate-200 dark:border-white/[0.06]"
          }`}
          style={{ borderTop: "none", borderLeft: "none", borderBottom: "none" }}
        >
          {isSideView && addConversationView ? (
            <CreateConversationModal
              addConversationView={addConversationView}
              users={users}
              selectedUsers={selectedUsers}
              setSelectedUsers={setSelectedUsers}
              handleCreateConversation={handleCreateConversation}
              isCreating={isCreatingConversation}
            prepareResource={!!activeShare}
            resourceTitle={activeShare?.resourceTitle}
              onClose={closeRecipientPicker}
              chatClose={() => onCloseSideChat?.()}
              inline
              selectionMode="multiple"
              showOrganizations={isSuperAdmin}
            />
          ) : (
            <>
              <div className={`shrink-0 ${isSideView ? "px-3 pb-2.5 pt-2.5" : "px-5 pb-3 pt-4"}`}>
                {!isSideView && (
                  <div className="min-w-0">
                    <h2 className="m-0 text-[15px] font-semibold text-slate-900 dark:text-slate-100">Messages</h2>
                    <p className="m-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">Conversations and channels</p>
                  </div>
                )}

                <div className={`dark-input-borderless ${isSideView ? "mt-0" : "mt-3"} flex items-center gap-2 rounded-xl bg-slate-100 px-3 transition-all focus-within:ring-2 focus-within:ring-[#7E57C2]/20 dark:bg-slate-800`}>
                  <Search size={15} className="shrink-0 text-slate-400" />
                  <input
                    type="search"
                    placeholder="Search conversations..."
                    value={searchTerm}
                    onChange={(event) => setSearchTearm(event.target.value)}
                    className="w-full border-none bg-transparent py-2 text-[13px] font-medium text-slate-700 outline-none shadow-none placeholder:font-normal placeholder:text-slate-400 dark:text-slate-100 dark:placeholder:text-slate-500"
                  />
                </div>

                <div className="chat-inbox-filter-bar mt-2 flex items-center gap-1 rounded-lg bg-slate-100 p-1 dark:bg-[#1d202b]">
                  {([
                    ["all", "All"],
                    ["unread", "Unread"],
                    ["channels", "Channels"],
                  ] as const).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        setInboxFilter(value);
                        setActiveTab(value === "channels" ? "Channels" : "Conversations");
                      }}
                      className={`chat-inbox-filter flex-1 rounded-md px-2 py-1.5 text-[11px] font-semibold transition-all ${
                        inboxFilter === value
                          ? "chat-inbox-filter-active bg-white text-[#6F49B5] shadow-sm dark:bg-[#282c39] dark:text-purple-300 dark:shadow-none"
                          : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {useUnifiedEndpoint && inboxFilter === "channels" && (
                <div className="shrink-0 border-b border-slate-100 px-3 py-2">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={showArchived}
                    aria-label="Show archived channels"
                    onClick={() => setShowArchived((current) => !current)}
                    className="group flex w-full items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#7E57C2]/20"
                  >
                    <span className="text-[11px] font-medium text-slate-600">Show archived channels</span>
                    <span
                      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                        showArchived ? "bg-[#7E57C2]" : "bg-slate-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                          showArchived ? "translate-x-4" : "translate-x-0.5"
                        }`}
                      />
                    </span>
                  </button>
                  {archivedPermissionError && (
                    <div className="mt-1 text-[10px] text-red-500">{archivedPermissionError}</div>
                  )}
                </div>
              )}

              {listTopSlot}

              <ul className="my_conversations_list min-h-0 flex-1 overflow-y-auto p-1.5 scrollbar-thin">
                {(loading || loadingUnified) ? (
                  <div className="flex h-full min-h-[220px] items-center justify-center text-xs text-slate-400">
                    Loading conversations...
                  </div>
                ) : filteredConversations.length === 0 ? (
                  <div className="flex h-full min-h-[220px] flex-col items-center justify-center px-6 text-center">
                    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                      <MessageSquare size={20} />
                    </div>
                    <p className="m-0 text-sm font-semibold text-slate-700">
                      {inboxFilter === "unread" ? "You're all caught up" : "No conversations found"}
                    </p>
                    <p className="mb-0 mt-1 text-[11px] leading-relaxed text-slate-500">
                      {inboxFilter === "unread"
                        ? "New messages will appear here when they arrive."
                        : "Try another search or start a new private message."}
                    </p>
                  </div>
                ) : (
                  filteredConversations.map((conv) => {
                    const isSelected = conv.id === selectedConversationId;
                    const read = isConversationRead(conv);
                    const lastMsg = conv.lastMessage as any | undefined;
                    const participantsExcludingMe = conv.participants.filter((participant) => participant.id !== userId);
                    const isGroup = participantsExcludingMe.length > 1;
                    const isChannelConversation =
                      conv.isChannel === true ||
                      (conv.isChannel === undefined && Boolean(conv.channel));
                    const isBotConversation = isAutomatedBotConversation(conv, userId);
                    const conversationTitle =
                      isChannelConversation && conv.channel?.name
                        ? conv.channel.name
                        : isBotConversation
                        ? "Waivera"
                        : getCompactConversationTitle(participantsExcludingMe);

                    let lastMsgText: React.ReactNode = "No messages yet";
                    if (lastMsg?.attachmentUrl) {
                      lastMsgText = (
                        <span className="flex items-center gap-1">
                          <Paperclip size={12} className="shrink-0" />
                          File attachment
                        </span>
                      );
                    } else if ((lastMsg?.messagePlaintext || lastMsg?.messageContent)?.trim()) {
                      const plaintext = (lastMsg.messagePlaintext || lastMsg.messageContent).trim();
                      const message =
                        lastMsg.messageType === MessageType.SYSTEM
                          ? formatSystemMessage(plaintext)
                          : plaintext;
                      lastMsgText = message.length > 100 ? `${message.substring(0, 100)}...` : message;
                    } else if (lastMsg?.messageDoc) {
                      const text = extractRichTextPreview(lastMsg.messageDoc);
                      if (text) lastMsgText = text.length > 100 ? `${text.substring(0, 100)}...` : text;
                    }

                    const typingUsers = typingMap[conv.id] || [];
                    const isSomeoneTyping = typingUsers.length > 0;
                    const unreadCount = typeof conv.unreadCount === "number" ? conv.unreadCount : read ? 0 : 1;
                    const reactionBadge = reactionBadges?.[conv.id];
                    const draftPreview = getConversationDraftPreview(userId, conv.id);
                    if (draftPreview) {
                      const preview = draftPreview.length > 90 ? `${draftPreview.substring(0, 90)}...` : draftPreview;
                      lastMsgText = (
                        <span className="block min-w-0 truncate italic text-slate-400 dark:text-slate-500">
                          <span className="font-semibold text-[#7E57C2]/80 dark:text-purple-300/80">Draft:</span>{" "}{preview}
                        </span>
                      );
                    }

                    return (
                      <li key={conv.id} className="mb-1 list-none">
                        <button
                          type="button"
                          onClick={() => handleSelectConversation(conv)}
                          onContextMenu={(event) => {
                            event.preventDefault();
                            setConversationContextMenu({
                              x: event.clientX,
                              y: event.clientY,
                              conversation: conv,
                              title: conversationTitle,
                            });
                          }}
                          className={`chat-conversation-row group flex w-full items-start gap-2.5 rounded-xl border-0 px-2.5 py-2 text-left shadow-none outline-none transition-colors focus:outline-none focus-visible:outline-none focus-visible:ring-0 ${
                            isSelected
                              ? "bg-[#7E57C2]/[0.08] dark:bg-purple-400/15"
                              : read
                                ? "hover:bg-slate-50 dark:hover:bg-[#1d202b]"
                                : "bg-[#7E57C2]/[0.035] hover:bg-[#7E57C2]/[0.07] dark:bg-purple-400/[0.06] dark:hover:bg-purple-400/10"
                          }`}
                        >
                          <div className="relative mt-0.5 shrink-0">
                            {isBotConversation ? (
                              <div
                                className="chat-conversation-bot-avatar flex h-9 w-9 items-center justify-center rounded-xl border-0 bg-[#7E57C2]/10 text-[#7E57C2] shadow-none outline-none dark:bg-purple-400/15 dark:text-purple-300"
                                title="Automated Waivera notifications"
                                aria-label="Automated bot conversation"
                              >
                                <Bot size={18} strokeWidth={2.1} />
                              </div>
                            ) : isChannelConversation ? (
                              <div
                                className={`flex h-9 w-9 items-center justify-center rounded-xl ${
                                  conv.channel?.isArchived
                                    ? "bg-amber-50 text-amber-600"
                                    : "bg-[#7E57C2]/10 text-[#7E57C2]"
                                }`}
                              >
                                {conv.channel?.isArchived ? <Archive size={17} /> : <Hash size={17} />}
                              </div>
                            ) : isGroup ? (
                              <div className="chat-conversation-group-avatar flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-500 dark:bg-[#282c39] dark:text-slate-300">
                                <Users size={17} />
                              </div>
                            ) : (
                              <MilestoneAvatar
                                userId={participantsExcludingMe[0]?.id || ""}
                                displayName={getParticipantLabel(participantsExcludingMe[0])}
                                size="xxs"
                                alternate
                              />
                            )}
                            {!read && (
                              <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#7E57C2] dark:border-[#171a24]" />
                            )}
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p
                                className={`m-0 block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] ${read ? "font-medium text-slate-800 dark:text-slate-200" : "font-semibold text-slate-900 dark:text-slate-100"}`}
                                title={conversationTitle}
                              >
                                {conversationTitle}
                              </p>
                              <span className={`shrink-0 text-[10px] ${read ? "text-slate-400" : "font-semibold text-[#7E57C2]"}`}>
                                {formatTime(conv.lastMessage?.createDate)}
                              </span>
                            </div>

                            <div className="mt-0.5 flex items-center gap-1.5">
                              {conv.activeMeeting?.roomId && !isSomeoneTyping ? (
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(event) => handleJoinActiveMeeting(event, conv)}
                                  onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                      event.preventDefault();
                                      handleJoinActiveMeeting(event, conv);
                                    }
                                  }}
                                  className="inline-flex min-w-0 flex-1 items-center gap-1.5 truncate text-[11.5px] font-semibold text-[#6847A3] hover:text-[#7E57C2] focus:outline-none"
                                  aria-label="Join active video call"
                                >
                                  <span className="relative flex h-2 w-2 shrink-0">
                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#7E57C2] opacity-35" />
                                    <span className="relative inline-flex h-2 w-2 rounded-full bg-[#7E57C2]" />
                                  </span>
                                  <Video size={12.5} className="shrink-0" />
                                  <span className="truncate">Call in progress</span>
                                  <span className="shrink-0 text-[10.5px]">· Join</span>
                                </span>
                              ) : (
                                <div className={`min-w-0 flex-1 truncate text-[11.5px] ${isSomeoneTyping ? "font-medium italic text-[#7E57C2] dark:text-purple-300" : read ? "text-slate-500 dark:text-slate-400" : "font-medium text-slate-700 dark:text-slate-300"}`}>
                                  {isSomeoneTyping
                                    ? typingUsers.length > 1
                                      ? "Several people are typing..."
                                      : `${conv.participants.find((participant) => participant.id === typingUsers[0])?.displayName || "Someone"} is typing...`
                                    : lastMsgText}
                                </div>
                              )}
                              {reactionBadge && reactionBadge.count > 0 && (
                                <span
                                  data-testid="conversation-reaction-badge"
                                  title={formatReactionCount(reactionBadge.count)}
                                  className="flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-[#7E57C2]/10 px-1 text-[11px] leading-none text-[#6D4DB3] dark:bg-purple-400/15 dark:text-purple-300"
                                >
                                  <span aria-hidden="true" className="flex items-center gap-0.5">
                                    {reactionBadge.emoji || <Smile size={11} strokeWidth={2.2} />}
                                    {reactionBadge.count > 1 && <span className="text-[10px] font-semibold tabular-nums">{reactionBadge.count > 99 ? "99+" : reactionBadge.count}</span>}
                                  </span>
                                  <span className="sr-only">{formatReactionCount(reactionBadge.count)}</span>
                                </span>
                              )}
                              {!read && unreadCount > 0 && (
                                <span className="flex min-w-[18px] shrink-0 items-center justify-center rounded-full bg-[#7E57C2] px-1.5 py-0.5 text-[9px] font-semibold text-white">
                                  {unreadCount > 99 ? "99+" : unreadCount}
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>

              <div className="dark-surface shrink-0 bg-white p-2.5 dark:bg-slate-900">
                <button
                  ref={conversationRef}
                  type="button"
                  onClick={() => {
                    setSelectedUsers([]);
                    setRecipientOwner(recipientOwnerKey);
                    setAddConversationView(true);
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-indigo-50 hover:text-indigo-700 dark:bg-[#1d202b] dark:text-slate-200 dark:hover:bg-[#282c39] dark:hover:text-purple-300"
                  aria-label="New private message"
                >
                  <Plus size={17} strokeWidth={2.25} />
                  <span>New message</span>
                </button>
              </div>
            </>
          )}
        </div>
        {conversationContextMenu && createPortal(
          <div
            ref={conversationContextMenuRef}
            role="menu"
            aria-label={`Actions for ${conversationContextMenu.title}`}
            className="fixed z-[10000] min-w-52 overflow-hidden rounded-lg bg-white py-1 shadow-xl outline-none ring-0 dark:bg-slate-800"
            style={{
              left: Math.max(8, Math.min(conversationContextMenu.x, window.innerWidth - 220)),
              top: Math.max(8, Math.min(conversationContextMenu.y, window.innerHeight - 100)),
            }}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => void handleMarkConversationAsRead(conversationContextMenu.conversation)}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <CheckCheck size={15} />
              Mark conversation as read
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => void handleCopyConversationName(conversationContextMenu.title)}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <Copy size={15} />
              Copy Conversation Name
            </button>
          </div>,
          document.body,
        )}
      </>
    );
  }
);

export default ConversationList;
